/**
 * SVG → unit-box glyph segments.
 *
 * Sketch-object glyph SVGs are bundled as raw strings (Vite `?raw`), parsed
 * once with three's SVGLoader, tessellated into polylines and normalized into
 * the unit box `[-0.5, 0.5]²` (each axis independently, so an object's
 * width/depth properties stretch the symbol like a CAD block). +y in the unit
 * glyph corresponds to the frame's `norm` (SVG y-down is flipped).
 *
 * Only path/shape OUTLINES are used (no fills), matching the line-drawn
 * floor-plan style of the door/window glyphs. Keep glyph SVGs to plain
 * paths/shapes — SVGLoader does not handle text or filters.
 */

import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';

/** Curve tessellation density per subpath. */
const CURVE_DIVISIONS = 16;

const unitGlyphCache = new Map<string, Float32Array>();

/**
 * Parse (and cache) an SVG source into unit-box line segments, flat
 * `[ax,ay, bx,by, ...]` — every 4 numbers = one segment.
 */
export function getUnitGlyph(assetKey: string, svgSource: string): Float32Array {
  const cached = unitGlyphCache.get(assetKey);
  if (cached) return cached;

  const { paths } = new SVGLoader().parse(svgSource);
  const polylines: Array<Array<{ x: number; y: number }>> = [];
  for (const path of paths) {
    for (const subPath of path.subPaths) {
      const points = subPath.getPoints(CURVE_DIVISIONS);
      if (points.length >= 2) polylines.push(points.map((p) => ({ x: p.x, y: p.y })));
    }
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const line of polylines) {
    for (const p of line) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }

  if (polylines.length === 0 || !isFinite(minX)) {
    const empty = new Float32Array(0);
    unitGlyphCache.set(assetKey, empty);
    return empty;
  }

  const spanX = Math.max(maxX - minX, 1e-6);
  const spanY = Math.max(maxY - minY, 1e-6);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  let segmentCount = 0;
  for (const line of polylines) segmentCount += line.length - 1;

  const unit = new Float32Array(segmentCount * 4);
  let w = 0;
  for (const line of polylines) {
    for (let i = 0; i + 1 < line.length; i++) {
      // Normalize each axis into [-0.5, 0.5]; flip Y (SVG is y-down).
      unit[w++] = (line[i].x - cx) / spanX;
      unit[w++] = -(line[i].y - cy) / spanY;
      unit[w++] = (line[i + 1].x - cx) / spanX;
      unit[w++] = -(line[i + 1].y - cy) / spanY;
    }
  }

  unitGlyphCache.set(assetKey, unit);
  return unit;
}
